import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import Organizations from './pages/Organizations';
import Instances from './pages/Instances';
import People from './pages/People';
import FacialSearch from './pages/FacialSearch';
import Logs from './pages/Logs';
import Login from './pages/Login';
import Tokens from './pages/Tokens';
import Docs from './pages/Docs';
import { authAPI } from './services/api';
import './App.css';

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setCheckingAuth(false);
        return;
      }

      try {
        await authAPI.verify();
        setIsAuthenticated(true);
      } catch (err: any) {
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };

    verifyToken();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    navigate('/');
  };

  const handleNavigateToPeople = (organizationId: string) => {
    navigate(`/people/${organizationId}`);
  };

  if (checkingAuth) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>🔐 FaceAPI</h1>
          <p className="login-subtitle">Verificando sessão...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <h1 className="logo">
            FaceAPI
            <svg className="logo-icon" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </h1>
          <ul className="nav-menu">
            <li>
              <Link to="/" className={isActive('/') && location.pathname === '/' ? 'active' : ''}>
                Início
              </Link>
            </li>
            <li>
              <Link to="/tokens" className={isActive('/tokens') ? 'active' : ''}>
                Tokens
              </Link>
            </li>
            <li>
              <Link to="/instances" className={isActive('/instances') ? 'active' : ''}>
                Instâncias
              </Link>
            </li>
            <li>
              <Link to="/organizations" className={isActive('/organizations') ? 'active' : ''}>
                Organizações
              </Link>
            </li>
            <li>
              <Link to="/search" className={isActive('/search') ? 'active' : ''}>
                Busca Facial
              </Link>
            </li>
            <li>
              <Link to="/docs" className={isActive('/docs') ? 'active' : ''}>
                Documentação
              </Link>
            </li>
            <li>
              <button 
                className="logout-btn"
                onClick={handleLogout}
                title="Sair do sistema"
              >
                <svg className="logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sair
              </button>
            </li>
          </ul>
        </div>
      </nav>
      
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/instances" element={<Instances />} />
          <Route path="/organizations" element={<Organizations onNavigateToPeople={handleNavigateToPeople} />} />
          <Route path="/people/:organizationId" element={<People />} />
          <Route path="/logs/:organizationId" element={<Logs />} />
          <Route path="/tokens" element={<Tokens />} />
          <Route path="/search" element={<FacialSearch />} />
          <Route path="/search/:organizationId" element={<FacialSearch />} />
          <Route path="/docs" element={<Docs />} />
        </Routes>
      </main>

      <footer className="footer">
        <p>&copy; 2026 FaceAPI - Detecção e Reconhecimento Facial</p>
      </footer>
    </div>
  );
}

function HomePage() {
  return (
    <div className="home-page">
      <div className="hero">
        <h1>Bem-vindo ao FaceAPI 👤</h1>
        <p>Sistema Avançado de Detecção e Reconhecimento Facial</p>
        
        <div className="features">
          <Link to="/tokens" className="feature">
            <span className="feature-icon">🔑</span>
            <div className="feature-content">
              <h3>Tokens</h3>
              <p>Gerencie tokens de integração e acesso</p>
            </div>
          </Link>

          <Link to="/instances" className="feature">
            <span className="feature-icon">🖥️</span>
            <div className="feature-content">
              <h3>Instâncias</h3>
              <p>Configure conexões com MongoDB e Milvus</p>
            </div>
          </Link>
          
          <Link to="/organizations" className="feature">
            <span className="feature-icon">🏢</span>
            <div className="feature-content">
              <h3>Organizações</h3>
              <p>Gerencie organizações e agrupe pessoas</p>
            </div>
          </Link>
          
          <Link to="/search" className="feature">
            <span className="feature-icon">🔍</span>
            <div className="feature-content">
              <h3>Busca Facial</h3>
              <p>Encontre pessoas por reconhecimento facial</p>
            </div>
          </Link>
          
          <Link to="/docs" className="feature">
            <span className="feature-icon">📚</span>
            <div className="feature-content">
              <h3>Documentação</h3>
              <p>Documentação completa da API</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
